import React, { useEffect, useRef, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { Link, useHistory } from 'react-router-dom';
import { ApiService } from '../api/ApiService';
import { LoadHistoryInfo } from '../api/model/LoadHistory';
import { DateUtils } from '../utils/DateUtils';
import LoadHistoryFirstIn from '../components/walkthrough/individualPage/LoadHistoryFirstIn';
import { ChevronRight } from '../components/icons';
import LoadDetailHeader, { useTransparentHeaderContrast } from '../components/LoadDetailHeader';
import { useLogPageVisit } from '../utils/logPageVisit';
import { logUserAction } from '../utils/logUserAction';

const PAGE_SIZE = 24;

const LoadHistory: React.FC = () => {
  useLogPageVisit('LP Load History', 'User viewed the Load History page');
  const history = useHistory();
  const { onSheet, onIonScroll } = useTransparentHeaderContrast();
  const lastNumRef = useRef(0);
  const newLoadRef = useRef<string | null>(null);
  const [loads, setLoads] = useState<{ [key: string]: LoadHistoryInfo[] }>({});
  const [allLoaded, setAllLoaded] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setTimeout(() => setMounted(true), 30);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    fetchPage(true);
  }, []);

  const parseLoads = (
    data: LoadHistoryInfo[],
    existing: { [key: string]: LoadHistoryInfo[] }
  ) => {
    const acc: { [key: string]: LoadHistoryInfo[] } = {};
    Object.keys(existing).forEach(key => {
      acc[key] = [...existing[key]];
    });
    data.forEach(load => {
      const key = load.paidDate;
      acc[key] = [...(acc[key] || []), load];
    });
    return acc;
  };

  const fetchPage = (reset: boolean) => {
    const start = reset ? 0 : lastNumRef.current;
    if (reset) setLoading(true);
    else setLoadingMore(true);

    ApiService.getLoadHistory('loadHistory', { start, length: PAGE_SIZE }, true)
      .then(it => {
        setLoads(prev => {
          if (!reset) {
            const seen = new Set(
              Object.values(prev).flatMap(group => group.map(load => String(load.orderNumber)))
            );
            const firstNew = it.find(load => !seen.has(String(load.orderNumber)));
            newLoadRef.current = firstNew ? String(firstNew.orderNumber) : null;
          }
          return parseLoads(it, reset ? {} : prev);
        });
        setAllLoaded(it.length < PAGE_SIZE);
        lastNumRef.current = start + PAGE_SIZE;
      })
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  };

  useEffect(() => {
    const pending = newLoadRef.current;
    if (!pending || loadingMore) return;
    newLoadRef.current = null;
    const id = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-load="${CSS.escape(pending)}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => cancelAnimationFrame(id);
  }, [loads, loadingMore]);

  const goBack = () => {
    if (history.length > 1) history.goBack();
    else history.replace('/Home');
  };

  const allLoads = Object.keys(loads).flatMap(key => loads[key]);
  const totalLoads = allLoads.length;

  return (
    <IonPage>
      <LoadDetailHeader title="Load History" onBack={goBack} onSheet={onSheet} />

      <IonContent
        fullscreen
        className="design-a-content design-a-content--light"
        scrollEvents={true}
        onIonScroll={onIonScroll}
      >
        <LoadHistoryFirstIn>
          <div className={`design-a design-a--detail${mounted ? ' design-a--mounted' : ''}`}>
            <div className="home-hero history-hero">
              <div className="history-hero__eyebrow">Past deliveries</div>
              <div className="history-hero__title home-display">Load History</div>
              <div className="history-hero__meta">
                {totalLoads > 0
                  ? `${totalLoads} load${totalLoads === 1 ? '' : 's'}`
                  : loading
                    ? 'Loading…'
                    : 'No loads yet'}
              </div>
            </div>

            <div className="home-sheet history-sheet">
              {allLoads.length === 0 && !loading ? (
                <div className="scoreboard-empty">
                  <strong>No load history</strong>
                  <p>Completed loads will show up here.</p>
                </div>
              ) : null}

              {allLoads.length > 0 ? (
                <section className="history-period">
                  {allLoads.map((load, index) => {
                    const isFiled = !(load.emptyDate && !load.paperworkDate);
                    return (
                      <Link
                        key={`${load.orderNumber}-${index}`}
                        to={`/loadhistory/${load.orderNumber}`}
                        data-load={String(load.orderNumber)}
                        className={`history-load${isFiled ? '' : ' history-load--pending'}`}
                        onClick={() => logUserAction('LP Load History View', `User viewed load ${load.orderNumber}`)}
                      >
                        <div className="history-load__body">
                          <div className="history-load__top">
                            <div className="history-load__number">#{load.orderNumber}</div>
                            {!isFiled ? (
                              <span className="history-load__tag">Paperwork pending</span>
                            ) : null}
                          </div>
                          <div className="history-load__meta">
                            Completed {DateUtils.formatDate(load.emptyDate) || '—'}
                            {isFiled && load.paperworkDate
                              ? ` · Paperwork ${DateUtils.formatDate(load.paperworkDate)}`
                              : ''}
                          </div>
                          {load.paidDate ? (
                            <div className="history-load__meta history-load__meta--muted">
                              Paid {DateUtils.formatDate(load.paidDate)}
                            </div>
                          ) : null}
                        </div>
                        <ChevronRight />
                      </Link>
                    );
                  })}
                </section>
              ) : null}

              {!allLoaded && allLoads.length > 0 ? (
                <div className="history-load-more-wrap">
                  <p className="history-load-more__hint">
                    Showing {totalLoads} loads. Older loads come in batches of {PAGE_SIZE}.
                  </p>
                  <button
                    type="button"
                    className="history-load-more"
                    onClick={() => fetchPage(false)}
                    disabled={loadingMore}
                  >
                    {loadingMore ? 'Loading older loads…' : 'Load older loads'}
                  </button>
                </div>
              ) : allLoads.length > 0 ? (
                <p className="history-load-more__hint history-load-more__hint--end">
                  That’s all {totalLoads} loads on file.
                </p>
              ) : null}
            </div>
          </div>
        </LoadHistoryFirstIn>
      </IonContent>
    </IonPage>
  );
};

export default LoadHistory;
